import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';

export type FiltersOptions = {
  campaignId?: number;
  presentationType?: string;
};

export const useFilters = (options: FiltersOptions) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/filters',
    {
      params: {
        query: {
          campaign_id: options.campaignId,
          presentation_type: options.presentationType
        }
      },
      queryOpts: {
        enabled: enabled || options.campaignId === undefined // Allow call even without campaign for initial load
      }
    }
  );

  return {
    isLoadingFilters: isLoading,
    filters: {
      availablePresentations: data?.available_presentations || [],
      campaigns: data?.campaigns || [],
      negociants: data?.negociants || [],
      saleTypes: data?.sale_types || [],
      wines: data?.wines || []
    },
    refetchFilters: refetch
  };
};

export type RawDataOptions = {
  campaignId?: number;
  saleTypeId?: number;
  wineId?: number;
  negociantId?: number;
};

export const useRawData = (options: RawDataOptions) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/raw_data',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1,
          wine_id: options.wineId || -1,
          negociant_id: options.negociantId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingRawData: isLoading,
    rawData: data || { rows: [] },
    refetchRawData: refetch
  };
};

export type PresentationKpisOptions = {
  campaignId?: number;
  negociantId?: number;
  wineId?: number;
};

export const usePresentationKpis = (options: PresentationKpisOptions) => {
  const enabled =
    options.campaignId !== undefined &&
    options.campaignId !== null &&
    options.negociantId !== undefined &&
    options.negociantId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/negociant/{negociant_id}',
    {
      params: {
        path: {
          campaign_id: options.campaignId!,
          negociant_id: options.negociantId!
        },
        query: {
          wine_id: options.wineId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingPresentationKpis: isLoading,
    presentationKpis: data || {},
    refetchPresentationKpis: refetch
  };
};

export type TopNegociantsOptions = {
  campaignId?: number;
  saleTypeId?: number;
  wineId?: number;
};

export const useTopNegociants = (options: TopNegociantsOptions) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/negociants',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1,
          wine_id: options.wineId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingTopNegociants: isLoading,
    topNegociants: data || {},
    refetchTopNegociants: refetch
  };
};

export type TopCustomersOptions = {
  campaignId?: number;
  saleTypeId?: number;
  wineId?: number;
};

export const useTopCustomers = (options: TopCustomersOptions) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/customers',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1,
          wine_id: options.wineId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingTopCustomers: isLoading,
    topCustomers: data || {},
    refetchTopCustomers: refetch
  };
};

export type SynthesisKpiOptions = {
  campaignId?: number;
  saleTypeId?: number;
  wineId?: number;
};

export const useSynthesisKpi = (options: SynthesisKpiOptions) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/stats/kpis',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1,
          wine_id: options.wineId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingSynthesisKpi: isLoading,
    synthesisKpi: data || {},
    refetchSynthesisKpi: refetch
  };
};

export type SynthesisSalesByCountryOptions = {
  campaignId?: number;
  saleTypeId?: number;
  wineId?: number;
};

export const useSynthesisSalesByCountry = (
  options: SynthesisSalesByCountryOptions
) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/stats/sales-by-country',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1,
          wine_id: options.wineId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingSynthesisSalesByCountry: isLoading,
    synthesisSalesByCountry: data || {},
    refetchSynthesisSalesByCountry: refetch
  };
};

export type SynthesisSalesByWineOptions = {
  campaignId?: number;
  saleTypeId?: number;
};

export const useSynthesisSalesByWine = (
  options: SynthesisSalesByWineOptions
) => {
  const enabled =
    options.campaignId !== undefined && options.campaignId !== null;

  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/negociant/campaign/{campaign_id}/stats/sales-by-wine',
    {
      params: {
        path: {
          campaign_id: options.campaignId!
        },
        query: {
          sale_type_id: options.saleTypeId || -1
        }
      },
      queryOpts: {
        enabled: enabled
      }
    }
  );

  return {
    isLoadingSynthesisSalesByWine: isLoading,
    synthesisSalesByWine: data || {},
    refetchSynthesisSalesByWine: refetch
  };
};
